import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import IconBack from "app/pages/.shared/static/icons/IconBack";
import "./SearchMyBooking.scss";
import Button from "app/pages/.shared/form/Button";
import { Form, Formik } from "formik";
import InputFormik from "app/pages/.shared/form/InputFormik";
import {
	defaultValues,
	validationSchema,
} from "app/pages/Account/SearchMyBooking/SearchMyBookingFormSchema";
import AppGlobalsContext from "app/AppGlobalsContext";
import RelativeLink from "app/pages/.shared/RelativeLink";
import PropTypes from "prop-types";
import { BRANDS, SEARCH_MY_BOOKINGS_STATUS } from "app/constants";
import RecoverBookingDataContainer from "app/pages/CorporateSite/RecoverBookingData/RecoverBookingDataContainer";
import IconCrossDark from "app/pages/.shared/static/icons/IconCrossDark";
import IconPictoChevron from "app/pages/.shared/IconPictoChevron";
import LogoBrandContainer from "app/pages/.shared/LogoBrand/LogoBrandContainer";
import { arrow, FloatingArrow, offset, shift, useFloating } from "@floating-ui/react";

const SearchMyBooking = ({ onFormSuccess, searchMyBooking }) => {
	const [myBookingNotFound, setMyBookingNotFound] = useState(false);
	const [showBookingForm, setShowBookingForm] = useState(true);
	const [isOpen, setIsOpen] = useState(false);
	const arrowRef = useRef();
	const handleSubmit = useCallback((values, actions) => {
		actions.setSubmitting(false);
		onFormSuccess(values.booking, values.email);
	}, []);

	const onClickCross = useCallback(() => {
		setShowBookingForm(true);
	}, []);

	const handleRecoverLinkClick = useCallback(() => {
		setShowBookingForm(false);
	}, []);

	const { brand } = useContext(AppGlobalsContext);

	const isBrandEH = brand === BRANDS.EH || brand === BRANDS.EK;

	useEffect(() => {
		setMyBookingNotFound(searchMyBooking.status === SEARCH_MY_BOOKINGS_STATUS.NOT_FOUND);
	}, [searchMyBooking]);

	const { context, y, x, refs, strategy } = useFloating({
		placement: "top",
		strategy: "absolute",
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [
			offset(),
			shift(),
			arrow({
				element: arrowRef,
			}),
		],
	});

	return (
		<div className="search-my-booking">
			<div className="search-my-booking__header">
				<div
					className="search-my-booking__header-back"
					onClick={() => {
						window.history.back();
					}}
				>
					<IconBack />
				</div>
				<RelativeLink
					to={{ pathname: "/" }}
					className={classNames({
						"search-my-booking__header__logo": true,
						"search-my-booking__header__logo-small": isBrandEH,
					})}
				>
					<LogoBrandContainer />
				</RelativeLink>
			</div>
			{showBookingForm ? (
				<div className="search-my-booking__body">
					<header className="search-my-booking__headline">
						<FormattedMessage id="search.my.booking.headline" tagName="h3" />
					</header>
					<Formik
						enableReinitialize
						initialValues={defaultValues}
						validateOnChange={false}
						validateOnBlur={false}
						validationSchema={validationSchema}
						onSubmit={handleSubmit}
					>
						{({ errors, isSubmitting, setErrors }) => {
							const getFormGlobalError = errors => {
								let _error = { id: "error.form.generic" };
								if (errors.booking) {
									_error = errors.booking;
								} else if (errors.email) {
									_error = errors.email;
								} else if (myBookingNotFound) {
									_error = { id: "error.booking.not.found.error" };
								}
								return _error;
							};

							const resetFormError = () => {
								setErrors({});
								setMyBookingNotFound(false);
								setIsOpen(false);
							};

							return (
								<>
									<Form className="search-my-booking__form">
										<InputFormik
											id="booking"
											data-testid="booking-reference-input"
											name="booking"
											type="text"
											label={
												<FormattedMessage id="search.my.booking.booking.input.label" />
											}
										/>

										<InputFormik
											id="email"
											data-testid="email-input"
											name="email"
											type="text"
											label={
												<FormattedMessage id="search.my.booking.email.input.label" />
											}
										/>

										<div
											className="search-my-booking__forgotten-infos"
											onClick={handleRecoverLinkClick}
										>
											<FormattedMessage id="search.my.booking.forgotten.label" />
										</div>

										<div
											className="search-my-booking__form-footer"
											ref={refs.setReference}
										>
											<Button
												variant="primary"
												submit
												className="search-my-booking__button"
												loading={isSubmitting}
												data-testid="search-button"
											>
												<FormattedMessage id="search.my.booking.cta.label" />
											</Button>
											{(errors.booking ||
												errors.email ||
												myBookingNotFound) && (
												<div
													className="search-my-booking__popover"
													ref={refs.setFloating}
													style={{
														position: strategy,
														top: y + 10 ?? "",
														left: x ?? "",
													}}
												>
													<FloatingArrow
														ref={arrowRef}
														context={context}
														className="search-my-booking__popover-arrow"
													/>

													<div className="search-my-booking__popover-content">
														<div className="search-my-booking__popover-text">
															{errors.booking ||
															errors.email ||
															myBookingNotFound ? (
																<FormattedMessage
																	{...getFormGlobalError(errors)}
																/>
															) : (
																""
															)}
														</div>
														<div
															className="search-my-booking__popover-close"
															onClick={resetFormError}
														>
															<i className="icon icon--cross" />
														</div>
													</div>
												</div>
											)}
										</div>
									</Form>
								</>
							);
						}}
					</Formik>
					<div className="search-my-booking__footer">
						<RelativeLink
							className="nav__item-link"
							to={{ pathname: "/booking-auth" }}
							data-testid="switch-to-booking-auth-link"
						>
							<FormattedMessage id="search.my.booking.footer.label" />
							<IconPictoChevron />
						</RelativeLink>
					</div>
				</div>
			) : (
				<div className="search-my-booking__recover">
					<div className="search-my-booking__form-recover">
						<div className="search-my-booking__cross" onClick={onClickCross}>
							<IconCrossDark />
						</div>
						<RecoverBookingDataContainer onRecoverSuccess={onClickCross} />
					</div>
				</div>
			)}
		</div>
	);
};

SearchMyBooking.propTypes = {
	onFormSuccess: PropTypes.func,
	searchMyBooking: PropTypes.object,
};

export default memo(SearchMyBooking);
